// Shared date formatting utilities — always DD-MM-YYYY for Indonesian UX.

/**
 * Convert an ISO date "YYYY-MM-DD" (or full ISO datetime) to "DD-MM-YYYY".
 * Returns the original string if it can't be parsed.
 */
export function formatIdDate(value) {
  if (!value) return "";
  const s = String(value);
  // Simple YYYY-MM-DD (no time)
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  // Fallback: try Date parse
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
  }
  return s;
}

/**
 * Convert an ISO datetime "YYYY-MM-DDTHH:MM:SS(+TZ)" to "DD-MM-YYYY HH:mm".
 * Returns the original string if it can't be parsed.
 */
export function formatIdDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Auto-format Indonesian vehicle plate as user types.
 *
 * Accepts loose input (with or without dashes/spaces) and returns the
 * canonical `AREA-NUMBERS-SUFFIX` form:
 *   "bk1234ab"    → "BK-1234-AB"
 *   "B 1234 XY"   → "B-1234-XY"
 *   "BK-1234-AB"  → "BK-1234-AB"   (already correct — idempotent)
 *   "BK88"        → "BK-88"        (partial while still typing)
 *   "BK"          → "BK"           (partial, no digits yet)
 *
 * Format rules (Indonesian plates):
 *   - Area code : 1–2 leading letters (B, BK, BM, D, KT, dst.)
 *   - Numbers   : 1–4 digits
 *   - Suffix    : 0–3 trailing letters (optional)
 *
 * If the input doesn't match this shape (e.g. starts with a digit or has
 * an unusual sequence), the cleaned uppercase value is returned unchanged
 * — never blocks the user from typing.
 */
export function formatPlate(raw) {
  const clean = String(raw || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (!clean) return "";
  const m = clean.match(/^([A-Z]{1,2})(\d+)?([A-Z]+)?$/);
  if (!m) return clean;
  const [, area, digits, suffix] = m;
  const parts = [area];
  if (digits) parts.push(digits);
  if (suffix) parts.push(suffix);
  return parts.join("-");
}

